import React, { useMemo } from "react";
import { fetchQuery } from "react-relay";
import type { OnChangeValue, OptionProps, PlaceholderProps, SingleValue } from "react-select";

import { faTimes, faUser } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { debounce } from "lodash";
import { compact } from "lodash";

import { MeetWithContext } from "~/calendar/CalendarPage";
import { type Contact, ContactsQuery } from "~/common/EmailListInput";
import useToggle from "~/lib/hooks/useToggle";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { environment } from "~/models";
import { Avatar, Tooltip } from "~/ui";
import { ButtonLink, IconButton } from "~/ui/legacyFellowIndex";
import AsyncSelector, { components, type SelectorOption } from "~/ui/selectors/AsyncSelector";

import type { EmailListInputContactsQuery } from "~/__generated__/EmailListInputContactsQuery.graphql";

import participantStyles from "../EventForm/ParticipantsSelector.module.css";
import baseStyles from "./BasePanel.module.css";
import styles from "./MeetWith.module.css";

export type MeetWithUser = Contact;

type UserSelectorOption = SelectorOption & MeetWithUser;

function ParticipantOption(props: OptionProps<UserSelectorOption, false>) {
	const { data } = props;
	const user = useMemo(() => ({ ...data, fullName: data.fullName || data.email }), [data]);
	return (
		<components.Option {...props}>
			<Avatar user={user} className={styles.optionAvatar} />
			<span className={cx(participantStyles.optionText, styles.optionText)}>{user.fullName}</span>
		</components.Option>
	);
}

function NoopComponent() {
	return null;
}

function PlaceholderComponent(props: PlaceholderProps<UserSelectorOption, false>) {
	return (
		<components.Placeholder {...props}>
			{!props.isFocused && <FontAwesomeIcon icon={faUser} style={{ marginRight: "8px" }} />}
			Search for people
		</components.Placeholder>
	);
}

type SelectedUserProps = MeetWithUser & {
	onRemove: () => void;
};

function SelectedUser({ onRemove, ...userData }: SelectedUserProps) {
	const user = useMemo(() => ({ ...userData, fullName: userData.fullName || userData.email }), [userData]);
	return (
		<div className={participantStyles.participant}>
			<Avatar user={user} outline={user.isGuest ? "none" : "solid"} className={styles.avatar} />
			<div className={participantStyles.participantProfile}>
				<Tooltip content={user.fullName} placement="top" disabled={user.fullName ? user.fullName.length < 35 : true}>
					<span className={styles.userName}>{user.fullName}</span>
				</Tooltip>
			</div>
			<Tooltip content="Delete" placement="top">
				<IconButton icon={faTimes} onClick={onRemove} className={participantStyles.participantControls} />
			</Tooltip>
		</div>
	);
}

export default function MeetWith() {
	const { meetWithParticipants, setMeetWithParticipants } = React.useContext(MeetWithContext);
	const [users, setUsers] = React.useState<MeetWithUser[]>(meetWithParticipants);
	const [showAll, toggleShowAll] = useToggle(false);
	const debouncedSetMeetWithParticipants = React.useMemo(
		() => debounce(setMeetWithParticipants, 500),
		[setMeetWithParticipants],
	);

	const loadOptions = React.useCallback(
		async (searchString: string) => {
			const result = await fetchQuery<EmailListInputContactsQuery>(environment, ContactsQuery, {
				search: searchString.trim(),
				first: 10,
			}).toPromise();

			if (!result?.viewer.contacts) return [];
			const { contacts, account: acc } = result.viewer;

			let options: OnChangeValue<UserSelectorOption, true> = compact(
				contacts.edges.map(edge => (edge && edge.node ? edge.node : null)),
			).map(node => ({
				user: node.user,
				value: node.id,
				label: node.fullName,
				id: node.id,
				email: node.email,
				fullName: node.fullName,
				image: node.image,
				hasConfiguredUser: node.user?.isConfigured ?? false,
				isGuest: node.user?.isGuest, // user the contact user isGuest if available,
				isExternal:
					!node.user?.isGuest &&
					!acc.isPersonalType && // otherwise check if the email domains match ignoring case
					!acc.emailDomainList.some(domain => node?.email.toLowerCase().endsWith(`@${domain.toLowerCase()}`)),
			}));

			// Excluding previously added participants
			options = options.filter(({ id }) => !users.find(contact => id === contact.id));

			return options;
		},
		[users],
	);

	const onSelect = React.useCallback(
		(value: SingleValue<UserSelectorOption>) => {
			if (!value) return;
			setUsers(users => (!!users.find(u => u.id === value.id) ? users : [...users, value]));
			debouncedSetMeetWithParticipants(users => (!!users.find(u => u.id === value.id) ? users : [...users, value]));

			dispatchAnalyticsTrack("Meet with operation", { feature: "Calendar", action: "add", user: value });
		},
		[debouncedSetMeetWithParticipants],
	);

	const onRemoveUser = React.useCallback(
		(user: MeetWithUser) => () => {
			setUsers(users => users.filter(u => u.id !== user.id));
			setMeetWithParticipants(users => users.filter(u => u.id !== user.id));

			dispatchAnalyticsTrack("Meet with operation", { feature: "Calendar", action: "remove", user });
		},
		[setMeetWithParticipants],
	);

	const clearUsers = React.useCallback(() => {
		setUsers([]);
		setMeetWithParticipants([]);

		dispatchAnalyticsTrack("Meet with operation", { feature: "Calendar", action: "clear" });
	}, [setMeetWithParticipants]);

	return (
		<div className={styles.componentWrapper}>
			<div className={styles.meetWithHeaderWrapper}>
				<div className={baseStyles.header}>Meet with…</div>
				{users.length > 0 && (
					<div className={styles.meetWithClear} onClick={clearUsers}>
						Clear list
					</div>
				)}
			</div>

			<AsyncSelector
				loadOptions={loadOptions}
				value={null}
				components={{
					Option: props => (
						<ParticipantOption
							{...props}
							isDisabled={!!users.find(({ id }) => id === props.data.id) || props.isDisabled}
						/>
					),
					IndicatorsContainer: NoopComponent,
					IndicatorSeparator: NoopComponent,
					Placeholder: PlaceholderComponent,
				}}
				styles={{
					option: (base, { isFocused }) => ({
						...base,
						padding: "6px 8px",
						display: "flex",
						alignItems: "center",
						cursor: "pointer",
						...(isFocused
							? {
									background: "var(--veryMutedGrey)",
									borderRadius: "0.375rem",
								}
							: {}),
					}),
					control: (base, { isFocused }) => ({
						...base,
						border: `1px solid var(--${isFocused ? "vividRoyal" : "dividerColor"})`,
						borderRadius: "6px",
						minHeight: "32px",
						backgroundColor: `var(--secondLayerColor)`,
						cursor: "pointer",
						boxShadow: "none",
					}),
					valueContainer: base => ({
						...base,
						padding: "4px 12px",
						font: "var(--paragraph-4)",
					}),
				}}
				defaultOptions
				menuPosition="fixed"
				onChange={onSelect}
			/>
			<div className={styles.usersWrapper}>
				{(showAll ? users : users.slice(0, 5)).map(user => (
					<SelectedUser key={user.id} onRemove={onRemoveUser(user)} {...user} />
				))}
				{!showAll && users.length > 5 && (
					<ButtonLink onClick={toggleShowAll} className={participantStyles.showAllButton}>
						Show all
					</ButtonLink>
				)}
			</div>
		</div>
	);
}
